import React, { useEffect, useRef } from 'react';
import { Plus, Edit2, X, Save, Bold, Italic, Underline } from 'lucide-react';
import Portal from './Portal';

/**
 * Modal Novo/Editar post — layout alinhado a Agenda / Transações / Pets (Portal, sheet, cabeçalho + X).
 */
function BlogPostEditorModal({
  isOpen,
  editingPost,
  postForm,
  onClose,
  onSave,
  onFormChange,
  saving
}) {
  const contentEditableRef = useRef(null);
  const fontSizeSelectRef = useRef(null);

  useEffect(() => {
    if (isOpen && contentEditableRef.current && contentEditableRef.current.innerHTML !== postForm.conteudo) {
      contentEditableRef.current.innerHTML = postForm.conteudo || '';
    }
  }, [isOpen, postForm.conteudo]);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      const prevHtmlOverflow = document.documentElement.style.overflow;
      const prevBodyOverflow = document.body.style.overflow;
      const prevBodyPosition = document.body.style.position;
      const prevBodyTop = document.body.style.top;
      const prevBodyWidth = document.body.style.width;
      const prevBodyHeight = document.body.style.height;

      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      return () => {
        document.documentElement.style.overflow = prevHtmlOverflow;
        document.body.style.overflow = prevBodyOverflow;
        document.body.style.position = prevBodyPosition;
        document.body.style.top = prevBodyTop;
        document.body.style.width = prevBodyWidth;
        document.body.style.height = prevBodyHeight;
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const handleContentChange = () => {
    if (contentEditableRef.current) {
      let html = contentEditableRef.current.innerHTML;

      html = html
        .replace(/<div><br\s*\/?><\/div>/gi, '<div><br></div>')
        .replace(/<p><br\s*\/?><\/p>/gi, '<p><br></p>')
        .replace(/<div><\/div>/g, '<div><br></div>')
        .replace(/<p><\/p>/g, '<p><br></p>');

      html = html.replace(/(<(div|p)[^>]*>)([ ]+)/g, (match, openTag, _elementType, spaces) => {
        return openTag + spaces.replace(/ /g, '&nbsp;');
      });

      onFormChange('conteudo', html);
    }
  };

  const getSelectedFontSize = () => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0 || selection.toString().length === 0) {
      return '18';
    }

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;

    let element = container.nodeType === 3 ? container.parentElement : container;

    while (element && element !== contentEditableRef.current) {
      const fontSize = window.getComputedStyle(element).fontSize;
      if (fontSize && fontSize !== 'inherit' && fontSize !== 'initial') {
        const sizeInPx = parseFloat(fontSize);
        const availableSizes = [12, 14, 16, 18, 20, 24, 28, 32, 36];
        const closest = availableSizes.reduce((prev, curr) =>
          Math.abs(curr - sizeInPx) < Math.abs(prev - sizeInPx) ? curr : prev
        );
        return closest.toString();
      }
      element = element.parentElement;
    }

    return '18';
  };

  const handleSelectionChange = () => {
    if (fontSizeSelectRef.current) {
      const fontSize = getSelectedFontSize();
      fontSizeSelectRef.current.value = fontSize;
    }
  };

  const handleFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    contentEditableRef.current?.focus();
    handleContentChange();
  };

  const handleFontSize = (size) => {
    if (!contentEditableRef.current) return;

    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (selectedText) {
      const span = document.createElement('span');
      span.style.fontSize = `${size}px`;
      span.textContent = selectedText;

      range.deleteContents();
      range.insertNode(span);

      selection.removeAllRanges();

      handleContentChange();
    } else {
      document.execCommand('fontSize', false, '7');
      setTimeout(() => {
        const elements = contentEditableRef.current?.querySelectorAll('font[size="7"]');
        elements?.forEach((el) => {
          el.style.fontSize = `${size}px`;
          el.removeAttribute('size');
        });
        handleContentChange();
      }, 0);
    }

    contentEditableRef.current?.focus();
  };

  const handleTab = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      document.execCommand('insertText', false, '  ');
      handleContentChange();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const text = (e.clipboardData || window.clipboardData).getData('text/plain');

    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();

      const textNode = document.createTextNode(text);
      range.insertNode(textNode);

      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    handleContentChange();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[99999] flex items-stretch justify-center bg-black bg-opacity-50 p-0 sm:items-center sm:p-4"
        data-modal="blog-post-editor"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="relative flex h-dvh max-h-dvh min-h-0 w-full flex-col overflow-hidden bg-card-light dark:bg-card-dark sm:h-auto sm:min-h-0 sm:max-h-[90vh] sm:max-w-4xl sm:rounded-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative z-50 flex shrink-0 items-center justify-between gap-2 border-b border-border-light bg-card-light px-4 py-4 dark:border-border-dark dark:bg-card-dark sm:px-6">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {editingPost ? (
                <Edit2 className="h-5 w-5 shrink-0 text-foreground-light dark:text-foreground-dark" />
              ) : (
                <Plus className="h-5 w-5 shrink-0 text-foreground-light dark:text-foreground-dark" />
              )}
              <h2 className="min-w-0 text-xl font-bold text-foreground-light dark:text-foreground-dark">
                {editingPost ? 'Editar Post' : 'Novo Post'}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="shrink-0 p-1 text-muted-light transition-colors hover:text-foreground-light disabled:opacity-50 dark:text-muted-dark dark:hover:text-foreground-dark"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div
            className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6"
            style={{ overscrollBehavior: 'contain' }}
          >
            <div>
              <label className="label">Título</label>
              <input
                type="text"
                value={postForm.titulo}
                onChange={(e) => onFormChange('titulo', e.target.value)}
                className="input-field"
                placeholder="Digite o título do post"
              />
            </div>

            <div>
              <label className="label">Resumo</label>
              <textarea
                value={postForm.resumo}
                onChange={(e) => onFormChange('resumo', e.target.value)}
                className="input-field min-h-[5rem]"
                placeholder="Breve descrição do post (aparece na lista)"
                rows={3}
              />
            </div>

            <div>
              <span className="label mb-2 block">Conteúdo</span>

              <div className="sticky top-0 z-20 mb-0 flex flex-wrap items-center gap-2 rounded-t-lg border border-b-0 border-border-light bg-background-light p-2 dark:border-border-dark dark:bg-background-dark">
                <button
                  type="button"
                  onClick={() => handleFormat('bold')}
                  className="rounded p-2 transition-colors hover:bg-background-light dark:hover:bg-background-dark"
                  title="Negrito (Ctrl+B)"
                >
                  <Bold className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFormat('italic')}
                  className="rounded p-2 transition-colors hover:bg-background-light dark:hover:bg-background-dark"
                  title="Itálico (Ctrl+I)"
                >
                  <Italic className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFormat('underline')}
                  className="rounded p-2 transition-colors hover:bg-background-light dark:hover:bg-background-dark"
                  title="Sublinhado (Ctrl+U)"
                >
                  <Underline className="h-4 w-4" />
                </button>
                <div className="h-6 w-px bg-border-light dark:bg-border-dark" />
                <select
                  ref={fontSizeSelectRef}
                  onChange={(e) => handleFontSize(e.target.value)}
                  className="input-field !py-1.5 text-sm"
                  title="Tamanho da fonte"
                  defaultValue="18"
                >
                  <option value="12">12px</option>
                  <option value="14">14px</option>
                  <option value="16">16px</option>
                  <option value="18">18px</option>
                  <option value="20">20px</option>
                  <option value="24">24px</option>
                  <option value="28">28px</option>
                  <option value="32">32px</option>
                  <option value="36">36px</option>
                </select>
                <div className="flex-1" />
                <span className="text-xs text-muted-light dark:text-muted-dark">Use TAB para indentar</span>
              </div>

              <div
                ref={contentEditableRef}
                contentEditable
                onInput={handleContentChange}
                onKeyDown={handleTab}
                onPaste={handlePaste}
                onMouseUp={handleSelectionChange}
                onKeyUp={handleSelectionChange}
                className="min-h-[250px] w-full rounded-b-lg border border-border-light bg-card-light px-3 py-2 text-base leading-relaxed text-foreground-light focus:outline-none focus:ring-2 focus:ring-ring-light dark:border-border-dark dark:bg-card-dark dark:text-foreground-dark dark:focus:ring-ring-dark"
                style={{
                  whiteSpace: 'pre-wrap',
                  fontSize: '1.125rem'
                }}
                data-placeholder="Digite o conteúdo do post aqui..."
              />
              <style>{`
                [contenteditable][data-placeholder]:empty:before {
                  content: attr(data-placeholder);
                  color: #9ca3af;
                  pointer-events: none;
                }
              `}</style>
              <p className="mt-1 text-xs text-muted-light dark:text-muted-dark">
                O texto será exibido com a mesma formatação visual
              </p>
            </div>

            <div>
              <label className="label">URL da Imagem</label>
              <input
                type="url"
                value={postForm.urlImagem}
                onChange={(e) => onFormChange('urlImagem', e.target.value)}
                className="input-field"
                placeholder="https://exemplo.com/imagem.jpg"
              />
              {postForm.urlImagem && (
                <img
                  src={postForm.urlImagem}
                  alt="Preview"
                  className="mt-2 h-48 max-w-full rounded-lg border border-border-light object-cover dark:border-border-dark"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </div>

            <div>
              <label className="label">Tags (separadas por vírgula)</label>
              <input
                type="text"
                value={postForm.etiquetas}
                onChange={(e) => onFormChange('etiquetas', e.target.value)}
                className="input-field"
                placeholder="pet, cuidados, banho"
              />
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border-light bg-card-light px-4 py-4 dark:border-border-dark dark:bg-card-dark sm:px-6">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
              Cancelar
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-b-transparent" />
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Salvar</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

export default React.memo(BlogPostEditorModal);
